// GUF Options
import { IndicationYesNoOptions } from "../helpers/index";

import { BasicMetadata, BroIdMetadata, EditMetadata } from "./MetaData";
import { Task } from "./Task";

// 6.1 Bodemlustype (Soil Loop Type)
export const designLoopTypeOptions = [
  { value: "horizontaal", label: "Horizontaal" },
  { value: "korf", label: "Korf" },
  { value: "verticaal", label: "Verticaal" },
] as const;

export const designLoopTypeStandardValues = designLoopTypeOptions.map(({ value }) => value);
export type DesignLoopTypeOptions = (typeof designLoopTypeOptions)[number]["value"];

export const designScreenGeometryOptions = [
  { value: "Point", label: "Punt" },
  { value: "LineString", label: "Lijn" },
] as const;
export const designScreenGeometryOptionsType = designScreenGeometryOptions.map(
  ({ value }) => value
);
export type DesignScreenGeometryOptions = (typeof designScreenGeometryOptionsType)[number];

// 6.3 Filtertype (Filter Type)
export const filterTypeOptions = [
  { value: "nietVerticaal", label: "Niet verticaal" },
  { value: "verticaal", label: "Verticaal" },
  { value: "onbekend", label: "Onbekend", onlyImbroA: true },
] as const;

export const filterTypeStandardValues = filterTypeOptions.map(({ value }) => value);
export type FilterTypeOptions = (typeof filterTypeOptions)[number]["value"];

// 6.4 Gebruiksdoel (Usage Purpose)
export const usageTypeOptions = [
  { value: "agrarischeDoeleinden", label: "Agrarische doeleinden" },
  { value: "bemaling", label: "Bemaling" },
  { value: "brandblusvoorziening", label: "Brandblusvoorziening" },
  { value: "geslotenBodemenergiesysteem", label: "Gesloten Bodemenergiesysteem" },
  { value: "grondwatersanering", label: "Grondwatersanering" },
  { value: "industrieleToepassing", label: "Industriële toepassing" },
  { value: "openBodemenergiesysteem", label: "Open Bodemenergiesysteem" },
  { value: "openbareDrinkwatervoorziening", label: "Openbare drinkwatervoorziening" },
  { value: "overigeDoeleinden", label: "Overige doeleinden" },
] as const;

export const usageTypeStandardValues = usageTypeOptions.map(({ value }) => value);
export type UsageTypeOptions = (typeof usageTypeOptions)[number]["value"];

// 6.5 Installatiefunctie (Installation Function)
export const installationFunctionOptions = [
  { value: "geslotenBodemenergiesysteem", label: "Gesloten Bodemenergiesysteem" },
  { value: "onttrekkingEnRetournering", label: "Onttrekking en Retournering" },
  { value: "onttrekking", label: "Onttrekking" },
  { value: "infiltratie", label: "Infiltratie" },
  { value: "infiltratieEnOnttrekking", label: "Infiltratie en Onttrekking" },
  { value: "openBodemenergiesysteem", label: "Open Bodemenergiesysteem" },
] as const;

export const installationFunctionStandardValues = installationFunctionOptions.map(
  ({ value }) => value
);
export type InstallationFunctionOptions = (typeof installationFunctionOptions)[number]["value"];

// 6.6 KaderAanlevering (Delivery Framework)
export const gufDeliveryContextOptions = [
  { value: "waterwet", label: "Waterwet" },
  { value: "omgevingswet", label: "Omgevingswet" },
] as const;

export const gufDeliveryContextStandardValues = gufDeliveryContextOptions.map(({ value }) => value);
export type GUFDeliveryContextOptions = (typeof gufDeliveryContextOptions)[number]["value"];

// 6.7 Putfunctie (Well Function)
export const wellFunctionOptions = [
  { value: "infiltratie", label: "Infiltratie" },
  { value: "onttrekking", label: "Onttrekking" },
  { value: "retournering", label: "Retournering" },
  { value: "onbekend", label: "Onbekend", onlyImbroA: true },
] as const;

export const wellFunctionStandardValues = wellFunctionOptions.map(({ value }) => value);
export type WellFunctionOptions = (typeof wellFunctionOptions)[number]["value"];

// 6.8 Rechtstype (Legal Type)
export const legalTypeOptions = [
  { value: "melding", label: "Melding" },
  { value: "beschikking", label: "Beschikking" },
] as const;

export const legalTypeStandardValues = legalTypeOptions.map(({ value }) => value);
export type LegalTypeOptions = (typeof legalTypeOptions)[number]["value"];

// 6.9 Registratiestatus (Registration Status)
export const registrationStatusOptions = [
  { value: "geregistreerd", label: "Geregistreerd" },
  { value: "tijdelijkGeregistreerd", label: "Tijdelijk Geregistreerd" },
] as const;

export const registrationStatusStandardValues = registrationStatusOptions.map(({ value }) => value);
export type RegistrationStatusOptions = (typeof registrationStatusOptions)[number]["value"];

// 6.10 RelatieveTemperatuur (Relative Temperature)
export const relativeTemperatureOptions = [
  { value: "koud", label: "Koud" },
  { value: "warm", label: "Warm" },
  { value: "onbekend", label: "Onbekend", onlyImbroA: true },
] as const;

export const relativeTemperatureStandardValues = relativeTemperatureOptions.map(
  ({ value }) => value
);
export type RelativeTemperatureOptions = (typeof relativeTemperatureOptions)[number]["value"];

// 6.11 Verplaatsingsrichting (Displacement Direction)
export const displacementDirectionOptions = [
  { value: "inbrengen", label: "Inbrengen" },
  { value: "onttrekken", label: "Onttrekken" },
] as const;

export const displacementDirectionStandardValues = displacementDirectionOptions.map(
  ({ value }) => value
);
export type DisplacementDirectionOptions = (typeof displacementDirectionOptions)[number]["value"];

export type DesignLoop = {
  designLoopId: string;
  designLoopPos: string | undefined;

  loopType: DesignLoopTypeOptions | undefined;

  startDate: string;
  endDate: string;

  geometryType: "Point" | "LineString";
};

// A design screen does not yet have a geometry. This is only with the realisedScreen
export type DesignScreen = {
  screenType: FilterTypeOptions;
  designScreenTop: number;
  designScreenBottom: number | undefined;
};

export type DesignWell = {
  wellFunctions: WellFunctionOptions[];
  height: number;

  wellPos: string;
  geometryPubliclyAvailable: IndicationYesNoOptions | undefined;

  maximumWellDepth: number | undefined;
  maximumWellDepthPubliclyAvailable: IndicationYesNoOptions | undefined;

  maximumWellCapacity: number | undefined;
  relativeTemperature: RelativeTemperatureOptions | undefined;

  designScreen: DesignScreen | undefined;
};

export type DesignSurfaceInfiltration = {
  designSurfaceInfiltrationId: string;
  designSurfaceInfiltrationPos: string;
};

export type EnergyCharacteristicsData = {
  energyCold: number | undefined;
  energyWarm: number | undefined;
  maximumInfiltrationTemperatureWarm: number | undefined;
  averageInfiltrationTemperatureCold: number | undefined;
  averageInfiltrationTemperatureWarm: number | undefined;
  powerCold: number | undefined;
  powerWarm: number | undefined;
  power: number | undefined;
  averageColdWater: number | undefined;
  averageWarmWater: number | undefined;
  maximumYearQuantityCold: number | undefined;
  maximumYearQuantityWarm: number | undefined;
};

export type LicensedQuantity = {
  licensedInOut: DisplacementDirectionOptions;

  maximumPerHour: number | undefined;
  maximumPerDay: number | undefined;
  maximumPerMonth: number | undefined;
  maximumPerQuarter: number | undefined;
  maximumPerYear: number | undefined;
};

export type DesignInstallation = {
  designInstallationId: string;

  installationFunction: InstallationFunctionOptions;

  designInstallationPos: string;

  licensedQuantities: LicensedQuantity[];
  energyCharacteristics: EnergyCharacteristicsData | undefined;

  designLoops: DesignLoop[];
  designSurfaceInfiltrations: DesignSurfaceInfiltration[];
  designWells: DesignWell[];
};

export type GUFStartRegistrationData = {
  objectIdAccountableParty: string;
  deliveryContext: GUFDeliveryContextOptions;
  startTime: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  identificationLicence: string;
  legalType: LegalTypeOptions;
  primaryUsageType: UsageTypeOptions;
  secondaryUsageTypes: UsageTypeOptions[];
  humanConsumption: IndicationYesNoOptions;
  licensedQuantities: LicensedQuantity[];
  designInstallations: DesignInstallation[];
};

export type CreateGUFStartRegistrationTask = Task<GUFStartRegistrationData, BasicMetadata>;
export type EditGUFStartRegistrationTask = Task<GUFStartRegistrationData, EditMetadata>;

// ── GUF NewLicence ────────────────────────────────────────────────────────────
// Like StartRegistration but without objectIdAccountableParty and deliveryContext
export type GUFNewLicenceData = {
  startTime: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  identificationLicence: string;
  legalType: LegalTypeOptions;
  primaryUsageType: UsageTypeOptions;
  secondaryUsageTypes: UsageTypeOptions[];
  humanConsumption: IndicationYesNoOptions;
  licensedQuantities: LicensedQuantity[];
  designInstallations: DesignInstallation[];
};

export type CreateGUFNewLicenceTask = Task<GUFNewLicenceData, BroIdMetadata>;
export type EditGUFNewLicenceTask = Task<GUFNewLicenceData, EditMetadata>;

// ── Realised sub-types ────────────────────────────────────────────────────────

export type RealisedLoop = {
  realisedLoopId: string;
  endDepthBottom: number; // meters
  realisedLoopPos: string | undefined; // Position coordinates for Point geometry
  segments: string | undefined; // For Curve geometry
  geometryType: "Point" | "Curve";
  loopType: DesignLoopTypeOptions | undefined;
};

export type RealisedScreen = {
  realisedScreenId: string;
  screenType: FilterTypeOptions;
  topScreenDepth: number; // meters
  length: number; // meters
  realisedScreenPos: string | undefined; // For Point geometry
  segments: string | undefined; // For Curve geometry
  geometryType: "Point" | "Curve";
};

export type RealisedSurfaceInfiltration = {
  realisedSurfaceInfiltrationId: string;
  realisedSurfaceInfiltrationPos: string; // Position coordinates for Polygon geometry
};

export type RealisedWell = {
  realisedWellId: string;
  wellFunctions: WellFunctionOptions[]; // min 1, max 2
  height: number; // meters
  wellDepth: number; // meters
  wellPos: string;
  publiclyAvailable: IndicationYesNoOptions | undefined;
  relativeTemperature: RelativeTemperatureOptions | undefined;
  validity: string | undefined; // Not allowed in ExpandRealisedInstallation
  lifespan: string | undefined; // Not allowed in ExpandRealisedInstallation
  realisedScreens: RealisedScreen[];
};

export type RealisedScreenChanges = {
  realisedScreenId: string;
  topScreenDepth: number; // meters
};

export type RealisedInstallationFunction = {
  realisedInstallationId: string;
  installationFunction: InstallationFunctionOptions;
};

export type RealisedWellClosurePart = {
  realisedWellId: string;
};

// ── GUF AddRealisedInstallation ───────────────────────────────────────────────

export type GUFRealisedInstallationData = {
  realisedInstallationId: string;
  installationFunction: InstallationFunctionOptions;
  realisedLoopPos: string; // Position coordinates
  startValidity: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  realisedLoops: RealisedLoop[];
  realisedSurfaceInfiltrations: RealisedSurfaceInfiltration[];
  realisedWells: RealisedWell[];
};

export type CreateGUFAddRealisedInstallationTask = Task<GUFRealisedInstallationData, BroIdMetadata>;
export type EditGUFAddRealisedInstallationTask = Task<GUFRealisedInstallationData, EditMetadata>;

// ── GUF ExpandedRealisedInstallation ─────────────────────────────────────────
// Same data shape as AddRealisedInstallation, but validity/lifespan on RealisedWell
// are not allowed
export type GUFExpandedRealisedInstallationData = Omit<
  GUFRealisedInstallationData,
  "realisedWells"
> & {
  realisedWells: Omit<RealisedWell, "validity" | "lifespan">[];
};

export type CreateGUFExpandedRealisedInstallationTask = Task<
  GUFExpandedRealisedInstallationData,
  BroIdMetadata
>;
export type EditGUFExpandedRealisedInstallationTask = Task<
  GUFExpandedRealisedInstallationData,
  EditMetadata
>;

// ── GUF Height ────────────────────────────────────────────────────────────────

export type GUFHeightData = {
  realisedWellId: string;
  height: number; // meters
  wellDepth: number | undefined; // meters
  startValidity: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  realisedScreens: RealisedScreenChanges[];
};

export type CreateGUFHeightTask = Task<GUFHeightData, BroIdMetadata>;
export type EditGUFHeightTask = Task<GUFHeightData, EditMetadata>;

// ── GUF WellFunction ──────────────────────────────────────────────────────────

export type GUFWellFunctionData = {
  realisedWellId: string;
  wellFunctions: WellFunctionOptions[]; // min 1, max 2
  relativeTemperature: RelativeTemperatureOptions | undefined;
  eventDate: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  realisedInstallationFunction: RealisedInstallationFunction | undefined;
};

export type CreateGUFWellFunctionTask = Task<GUFWellFunctionData, BroIdMetadata>;
export type EditGUFWellFunctionTask = Task<GUFWellFunctionData, EditMetadata>;

// ── GUF ClosureRealisedPart ───────────────────────────────────────────────────

export type GUFClosureRealisedPartData = {
  realisedInstallationId: string;
  installationFunction: InstallationFunctionOptions | undefined;
  wellPos: string | undefined;
  endTime: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
  realisedWells: RealisedWellClosurePart[];
};

export type CreateGUFClosureRealisedPartTask = Task<GUFClosureRealisedPartData, BroIdMetadata>;
export type EditGUFClosureRealisedPartTask = Task<GUFClosureRealisedPartData, EditMetadata>;

// ── GUF Closure ───────────────────────────────────────────────────────────────

export type GUFClosureData = {
  endTime: string; // Format: YYYY-MM-DD, YYYY-MM, or YYYY
};

export type CreateGUFClosureTask = Task<GUFClosureData, BroIdMetadata>;
export type EditGUFClosureTask = Task<GUFClosureData, EditMetadata>;
